'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

function useIntersectionObserver(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setIsVisible(true);observer.unobserve(el);}},
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Our Wines', href: '/wines' },
  { label: 'Winemaking', href: '/winemaking' },
  { label: 'Team', href: '/team' },
  { label: 'Contact', href: '/contact' }];


  return (
    <>
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center transition-all duration-500"
      style={{ background: 'rgba(10,7,5,0.97)', backdropFilter: 'blur(20px)', opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none' }}>
        <button className="absolute top-6 right-6 p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
        </button>
        <nav className="flex flex-col gap-8 text-center">
          {navLinks.map((link) =>
          <Link key={link.href + link.label} href={link.href}
          className="font-display text-3xl font-medium italic text-[#F5EDD6] hover:text-[#C9A84C] transition-colors duration-300"
          onClick={() => setMenuOpen(false)}>{link.label}</Link>
          )}
        </nav>
        <div className="mt-10 text-[#9A8A72] text-xs font-sans uppercase tracking-[0.3em]">Est. 1850 · Pauillac, France</div>
      </div>

      <nav className="fixed z-50 top-0 inset-x-0 px-6 py-3 flex items-center justify-between transition-all duration-500"
      style={{ background: scrolled ? 'rgba(10,7,5,0.85)' : 'transparent', backdropFilter: scrolled ? 'blur(12px)' : 'none', borderBottom: scrolled ? '1px solid rgba(46,26,16,0.6)' : 'none' }}>
        <Link href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
              <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
              <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
            </svg>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
            <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) =>
          <Link key={link.href + link.label} href={link.href}
          className="text-sm font-medium text-[#9A8A72] hover:text-[#F5EDD6] transition-colors duration-300 relative group">
              {link.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link href="/contact" className="hidden md:inline-flex items-center gap-2 px-5 py-2 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
            Visit Estate
          </Link>
          <button className="lg:hidden p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" /></svg>
          </button>
        </div>
      </nav>
    </>);

}

function Footer() {
  return (
    <footer className="border-t border-[#2E1A10] bg-[#0A0705] pt-16 pb-8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 mb-12">
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
                <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
                <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
              </svg>
              <div className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
                <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
              </div>
            </div>
            <p className="text-[#9A8A72] text-sm leading-relaxed italic font-display">A historic Bordeaux château crafting wines of extraordinary depth and elegance since 1850.</p>
            <p className="text-[#9A8A72] text-xs mt-4 font-sans">Route des Châteaux, Saint Lambert<br />33250 Pauillac, Gironde, France</p>
          </div>
          <div className="flex flex-wrap gap-x-16 gap-y-8">
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Estate</span>
              <Link href="/" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Home</Link>
              <Link href="/about" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Heritage</Link>
              <Link href="/winemaking" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Winemaking</Link>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Discover</span>
              <Link href="/wines" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Wines</Link>
              <Link href="/team" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Team & Investors</Link>
              <Link href="/contact" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Visit Us</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[#2E1A10] pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-xs text-[#9A8A72] font-sans">© 2025 圣兰博之围 · Château Baron, Pauillac AOC · All rights reserved</span>
        </div>
      </div>
    </footer>);

}

interface Wine {
  number: string;
  name: string;
  vintage: string;
  type: string;
  score: number;
  blend: string;
  notes: string;
  image: string;
  alt: string;
}

const WINES: Wine[] = [
{
  number: '01',
  name: '圣兰博之围 Grand Vin',
  vintage: '2019',
  type: 'Pauillac AOC — Grand Vin',
  score: 98,
  blend: '82% Cab. Sauv. · 12% Merlot · 6% Cab. Franc · 22 months in French oak, 35% new',
  notes: 'Deep garnet. Blackcurrant, cedar, graphite, tobacco leaf. Monumental structure, silky tannins, 40+ year aging potential.',
  image: 'https://images.unsplash.com/photo-1583541318663-e2bbb41cac0c?w=800&q=80',
  alt: 'Dark red Bordeaux wine bottle with elegant label against stone cellar background'
},
{
  number: '02',
  name: 'Le Second de Baron',
  vintage: '2020',
  type: 'Pauillac AOC — Second Wine',
  score: 93,
  blend: '65% Cab. Sauv. · 25% Merlot · 10% Cab. Franc · 14 months in French oak, 20% new',
  notes: 'Approachable elegance. Red and dark fruits, spice, gentle cedar. Ready in 5–8 years. The unmistakable DNA of Baron Castle in a more accessible style.',
  image: 'https://images.unsplash.com/photo-1607720336444-7990bedc2bf9?w=800&q=80',
  alt: 'Elegant wine bottle with gold label resting on dark wooden surface'
},
{
  number: '03',
  name: 'Blanc de Baron',
  vintage: '2022',
  type: 'Bordeaux Blanc AOC',
  score: 94,
  blend: '80% Sauvignon Blanc · 20% Sémillon · 10 months in neutral French oak',
  notes: 'Citrus blossom, white peach, wet limestone. Vibrant acidity, long mineral finish. A rare and exceptional white from the estate.',
  image: 'https://images.unsplash.com/photo-1452443191217-08a4f1f238e6?w=800&q=80',
  alt: 'White wine bottle in vineyard at golden hour with rows of vines in background'
},
{
  number: '04',
  name: 'Rosé de Baron',
  vintage: '2023',
  type: 'Bordeaux Rosé AOC',
  score: 91,
  blend: '70% Merlot · 30% Cab. Sauv. · Direct press, stainless steel fermentation',
  notes: 'Pale salmon. Wild strawberry, rose petal, peach. Crisp, elegant, made for warm evenings. Exceptional freshness and length.',
  image: 'https://images.unsplash.com/photo-1599387227823-212b344c81af?w=800&q=80',
  alt: 'Château stone architecture facade with ivy-covered walls at Baron Castle estate'
},
{
  number: '05',
  name: 'Baron Réserve',
  vintage: '2018',
  type: 'Pauillac AOC — Réserve',
  score: 96,
  blend: '88% Cab. Sauv. · 12% Merlot · 24 months in French oak, 50% new',
  notes: 'Concentrated and powerful. Black plum, violets, iron, and cedar. Needs a decade of patience. A wine of extraordinary depth and longevity.',
  image: 'https://images.unsplash.com/photo-1733664094821-f215b68cfada?w=800&q=80',
  alt: 'Dark wine cellar with rows of French oak barrels aging Baron Réserve in stone vault'
},
{
  number: '06',
  name: 'Millésime Baron 2016',
  vintage: '2016',
  type: 'Pauillac AOC — Prestige Cuvée',
  score: 99,
  blend: '90% Cab. Sauv. · 10% Merlot · 28 months in French oak, 60% new',
  notes: 'A monument of Pauillac. Extraordinary depth, graphite, cassis, tobacco. Decades of evolution ahead. The greatest wine ever produced at the estate.',
  image: 'https://images.unsplash.com/photo-1657053488881-86657ee81726?w=800&q=80',
  alt: 'French oak wine barrels in stone château cellar with candlelight illumination'
}];


const VINTAGES = [
{ year: '2019', score: 98, desc: 'Perfect growing season. Benchmark vintage.', window: '2027–2055' },
{ year: '2018', score: 96, desc: 'Rich and generous. Exceptional ripeness.', window: '2025–2048' },
{ year: '2016', score: 99, desc: 'Historic. A monument of Pauillac.', window: '2030–2060' },
{ year: '2015', score: 97, desc: 'Opulent, hedonistic, approachable.', window: '2023–2045' },
{ year: '2010', score: 98, desc: 'Towering structure, extraordinary depth.', window: '2025–2060' },
{ year: '2005', score: 97, desc: 'Classic Médoc year. Cedar and cassis.', window: 'Drinking now' }];


function WineCard({ wine, index }: {wine: Wine;index: number;}) {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  return (
    <div ref={ref} className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-20 py-16 border-b border-[#2E1A10]"
    style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.8s ease, transform 0.8s ease', transitionDelay: `${index * 0.05}s` }}>
      {/* Left */}
      <div className="flex flex-col justify-between gap-8">
        <div>
          <span className="font-sans text-xs tracking-[0.25em] text-[#C9A84C] block mb-2">{wine.number}</span>
          <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-2" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.5rem)' }}>{wine.name}</h2>
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#9A8A72] block mb-1">{wine.type}</span>
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#9A8A72]">Vintage {wine.vintage}</span>
        </div>
        {/* Score */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 border border-[#C9A84C]/40 flex items-center justify-center">
            <span className="font-display text-xl font-medium text-[#C9A84C]">{wine.score}</span>
          </div>
          <div>
            <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-[#9A8A72] block">Points</span>
            <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-[#9A8A72]">Critical Score</span>
          </div>
        </div>
        {/* Image */}
        <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
          <img src={wine.image} alt={wine.alt} className="w-full h-full object-cover" style={{ filter: 'brightness(0.7)' }} />
        </div>
      </div>
      {/* Right */}
      <div className="flex flex-col justify-center">
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-5">{wine.blend}</p>
        <p className="text-[#9A8A72] leading-relaxed text-base mb-8">{wine.notes}</p>
        <div className="h-px bg-[#2E1A10]" />
      </div>
    </div>);

}

function VintageChart() {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  return (
    <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-14">
        <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">Vintage Chart — Grand Vin</span>
        <span className="w-8 h-px bg-[#2E1A10]" />
        <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#9A8A72]">Exceptional Vintages</span>
      </div>
      <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#2E1A10]"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
        {VINTAGES.map((v, i) =>
        <div key={v.year} className="border-r border-b border-[#2E1A10] p-8 last:border-r-0"
        style={{ transitionDelay: `${i * 0.08}s` }}>
            <div className="flex items-start justify-between mb-4">
              <span className="font-display text-4xl font-medium text-[#C9A84C]/40">{v.year}</span>
              <div className="w-12 h-12 border border-[#C9A84C]/30 flex items-center justify-center">
                <span className="font-display text-base font-medium text-[#C9A84C]">{v.score}</span>
              </div>
            </div>
            <p className="text-[#9A8A72] text-sm leading-relaxed mb-3">{v.desc}</p>
            <div className="flex items-center gap-2">
              <span className="w-3 h-px bg-[#C9A84C]" />
              <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-[#C9A84C]">Drinking Window: {v.window}</span>
            </div>
          </div>
        )}
      </div>
    </section>);

}

export default function WinesPage() {
  return (
    <main className="bg-[#0A0705] text-[#F5EDD6] overflow-x-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#C9A84C]" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">Our Collection · 6 Cuvées</span>
        </div>
        <h1 className="font-display font-medium text-[#F5EDD6] leading-none select-none" style={{ fontSize: 'clamp(4rem, 14vw, 14rem)' }}>
          The Collection
        </h1>
        <p className="font-display italic text-lg md:text-xl text-[#F5EDD6]/70 max-w-2xl leading-relaxed mt-6">
          Six expressions of Pauillac terroir — from our flagship Grand Vin to rare single-parcel releases that speak of specific soils and seasons.
        </p>
      </section>

      {/* Wines list */}
      <section className="px-6 lg:px-16 max-w-[1400px] mx-auto">
        {WINES.map((wine, i) => <WineCard key={wine.number} wine={wine} index={i} />)}
      </section>

      <VintageChart />

      <Footer />
    </main>);

}